import { inject } from "@angular/core";
import { type Route, Router } from "@angular/router";

import { checkIfLoggedInFn, NotArchivedPlan, notPmoEntity } from "../../core/guards";
import { isDsV1, isDsV2, isDsV3 } from "../../core/utils";
import { Constants } from "../../shared/constants/constants";
import { PageMode } from "../../shared/constants/enums";
import { ConsoleService } from "../../shared/services";
import { activitiesGuards, strategicActivitiesGuards } from "./_guards";

const strategicActivityRoutes: Route = {
  path: "strategic",
  canActivate: [checkIfLoggedInFn],
  children: [
    {
      path: "",
      canActivate: [
        () => {
          const router = inject(Router);
          if (isDsV2()) return true;
          else if (isDsV1()) return router.createUrlTree(["/console", "activities", "list"]);
          else return router.createUrlTree(["/console", "activity"]);
        },
        ...strategicActivitiesGuards.view,
      ],
      data: {
        title: "breadcrumbs.activity.strategic_list",
        breadcrumb: "breadcrumbs.activity.strategic_list",
        activityType: Constants.ACTIVITY_TYPES.STRATEGIC_ACTIVITY,
        tableConfList: Constants.MAIN_TABLE_LIST_CONF_KEY.NEW_DS_ACTIVITIES,
        itemTypeId: Constants.CONSTANT_PERMISSIONS.STRATEGIC_ACTIVITIES,
      },
      loadComponent: () => import("./activities-list/activities-list").then((m) => m.ActivitiesListComponent),
    },
    {
      path: "create",
      canActivate: [notPmoEntity, ...strategicActivitiesGuards.create, NotArchivedPlan()],
      data: {
        title: "breadcrumbs.activity.strategic_new",
        breadcrumb: "breadcrumbs.activity.strategic_new",
        type: Constants.ACTIVITY_TYPES.STRATEGIC_ACTIVITY,
        initiativeType: Constants.INITIATIVE_TYPES.STRATEGIC_PROJECT,
        pageMode: PageMode.create,
      },
      loadComponent: () => import("./activities-editor/activities-editor").then((m) => m.ActivitiesEditorComponent),
    },
    {
      path: "edit/:id",
      canActivate: [...strategicActivitiesGuards.edit, NotArchivedPlan()],
      data: {
        title: "breadcrumbs.activity.strategic_edit",
        breadcrumb: "breadcrumbs.activity.strategic_edit",
        type: Constants.ACTIVITY_TYPES.STRATEGIC_ACTIVITY,
        initiativeType: Constants.INITIATIVE_TYPES.STRATEGIC_PROJECT,
        pageMode: PageMode.edit,
      },
      loadComponent: () => import("./activities-editor/activities-editor").then((m) => m.ActivitiesEditorComponent),
    },
    {
      path: ":id",
      canActivate: [...strategicActivitiesGuards.view],
      data: {
        title: "activities.strategic",
        breadcrumb: "breadcrumbs.activity.strategic_view",
        type: Constants.ACTIVITY_TYPES.STRATEGIC_ACTIVITY,
      },
      loadComponent: () => import("./activities-view/activities-view").then((m) => m.ActivitiesViewComponent),
    },
  ],
};

const operationalActivityRoutes: Route = {
  path: "operational",
  canActivate: [checkIfLoggedInFn],
  children: [
    {
      path: "",
      canActivate: [
        () => {
          const router = inject(Router);
          if (isDsV2()) return true;
          else if (isDsV1()) return router.createUrlTree(["/console", "activities", "list"]);
          else return router.createUrlTree(["/console", "activity"]);
        },
        ...activitiesGuards.view,
      ],
      data: {
        title: "breadcrumbs.activity.operational_list",
        breadcrumb: "breadcrumbs.activity.operational_list",
        activityType: Constants.ACTIVITY_TYPES.OPERATIONAL_ACTIVITY,
        tableConfList: Constants.MAIN_TABLE_LIST_CONF_KEY.NEW_DS_ACTIVITIES,
        itemTypeId: Constants.CONSTANT_PERMISSIONS.ACTIVITIES,
      },
      loadComponent: () => import("./activities-list/activities-list").then((m) => m.ActivitiesListComponent),
    },
    {
      path: "create",
      canActivate: [notPmoEntity, ...activitiesGuards.create, NotArchivedPlan()],
      data: {
        title: "breadcrumbs.activity.operational_new",
        breadcrumb: "breadcrumbs.activity.operational_new",
        type: Constants.ACTIVITY_TYPES.OPERATIONAL_ACTIVITY,
        initiativeType: Constants.INITIATIVE_TYPES.REGULAR_TASK,
        pageMode: PageMode.create,
      },
      loadComponent: () => import("./activities-editor/activities-editor").then((m) => m.ActivitiesEditorComponent),
    },
    {
      path: "edit/:id",
      canActivate: [...activitiesGuards.edit, NotArchivedPlan()],
      data: {
        title: "breadcrumbs.activity.operational_edit",
        breadcrumb: "breadcrumbs.activity.operational_edit",
        type: Constants.ACTIVITY_TYPES.OPERATIONAL_ACTIVITY,
        initiativeType: Constants.INITIATIVE_TYPES.REGULAR_TASK,
        pageMode: PageMode.edit,
      },
      loadComponent: () => import("./activities-editor/activities-editor").then((m) => m.ActivitiesEditorComponent),
    },
    {
      path: ":id",
      canActivate: [...activitiesGuards.view],
      data: {
        title: "activities.operational",
        breadcrumb: "breadcrumbs.activity.operational_view",
        type: Constants.ACTIVITY_TYPES.OPERATIONAL_ACTIVITY,
      },
      loadComponent: () => import("./activities-view/activities-view").then((m) => m.ActivitiesViewComponent),
    },
  ],
};

export const ACTIVITIES_ROUTES: Route[] = [
  {
    path: "activities",
    canActivate: [checkIfLoggedInFn],
    children: [
      {
        path: "",
        canActivate: [
          checkIfLoggedInFn,
          () => {
            const router = inject(Router);
            if (isDsV2()) return true;
            else if (isDsV1()) return router.createUrlTree(["/console", "activities", "list"]);
            else return router.createUrlTree(["/console", "activity"]);
          },
        ],
        data: {
          title: "breadcrumbs.activity.list",
          breadcrumb: "breadcrumbs.activity.list",
          onCycleChange: (_router: Router, consoleService: ConsoleService) => {
            if (!isDsV3()) consoleService.init();
          },
          onEntityChange: (router: Router) => router.navigateByUrl("console/activities"),
        },
        loadComponent: () => import("../../shared/components").then((m) => m.SubmenuComponent),
      },
      strategicActivityRoutes,
      operationalActivityRoutes,
      {
        path: "list",
        canActivate: [
          checkIfLoggedInFn,
          () => {
            const router = inject(Router);
            if (isDsV1()) return true;
            else if (isDsV2()) return router.createUrlTree(["/console", "activities"]);
            else return router.createUrlTree(["/console", "activity"]);
          },
          ...activitiesGuards.view,
        ],
        data: {
          title: "breadcrumbs.activity.list",
          breadcrumb: { skip: true },
          tableConfList: Constants.MAIN_TABLE_LIST_CONF_KEY.ACTIVITIES,
          itemTypeId: Constants.CONSTANT_PERMISSIONS.ACTIVITIES,
          activityType: Constants.ACTIVITY_TYPES.OPERATIONAL_ACTIVITY,
        },
        loadComponent: () => import("./activities-list/activities-list").then((m) => m.ActivitiesListComponent),
      },
      {
        path: "create",
        canActivate: [notPmoEntity, checkIfLoggedInFn, ...activitiesGuards.create, NotArchivedPlan()],
        data: {
          title: "breadcrumbs.activity.new",
          breadcrumb: "breadcrumbs.activity.new",
          initiativeType: Constants.INITIATIVE_TYPES.REGULAR_TASK,
          type: Constants.ACTIVITY_TYPES.OPERATIONAL_ACTIVITY,
          pageMode: PageMode.create,
        },
        loadComponent: () => import("./activities-editor/activities-editor").then((m) => m.ActivitiesEditorComponent),
      },
      {
        path: "edit/:id",
        canActivate: [checkIfLoggedInFn, ...activitiesGuards.edit, NotArchivedPlan()],
        data: {
          title: "breadcrumbs.activity.edit",
          breadcrumb: "breadcrumbs.activity.edit",
          type: Constants.ACTIVITY_TYPES.OPERATIONAL_ACTIVITY,
          initiativeType: Constants.INITIATIVE_TYPES.REGULAR_TASK,
          pageMode: PageMode.edit,
        },
        loadComponent: () => import("./activities-editor/activities-editor").then((m) => m.ActivitiesEditorComponent),
      },
      {
        path: ":id",
        canActivate: [
          checkIfLoggedInFn,
          () => {
            const router = inject(Router);
            if (isDsV1()) return true;
            else if (isDsV2()) return router.createUrlTree(["/console", "activities"]);
            else return router.createUrlTree(["/console", "activity"]);
          },
          ...activitiesGuards.view,
        ],
        data: {
          title: "tasks.activity",
          breadcrumb: "breadcrumbs.activity.view",
          type: Constants.ACTIVITY_TYPES.OPERATIONAL_ACTIVITY,
        },
        loadComponent: () => import("./activities-view/activities-view").then((m) => m.ActivitiesViewComponent),
      },
    ],
  },
];
